// shopifyItems.js
const DEFAULT_QUERY = `
  query Items($first: Int!, $after: String) {
	metaobjects(type: "item", first: $first, after: $after) {
	  edges {
		cursor
		node {
		  handle
		  priority: field(key: "priority") { value }
		  onlineStoreUrl
		  title_ja: field(key: "title_ja") { value }
		  title_en: field(key: "title_en") { value }
		  description_ja: field(key: "description_ja") { value }
		  description_en: field(key: "description_en") { value }
		  is_black: field(key: "is_black") {value}
		  is_gray: field(key: "is_gray") {value}
		  initials: field(key: "initials") {value}
		  category: field(key: "category") {
			reference {
			  __typename
			  ... on Metaobject {
				handle
				type
				fields {
				  key
				  value
				}
			  }
			}
		  }
		  images: field(key: "images") {
			references(first: 50) {
			  nodes {
				__typename
				... on MediaImage {
				  image { url altText width height }
				}
			  }
			}
		  }
		}
	  }
	  pageInfo { hasNextPage }
	}
  }
`;

/**
 * Shopify Storefront API クライアントを作成
 * @param {Object} opts
 * @param {string} opts.shop - myshopify ドメインのサブドメイン（例: "dvf9jg-fz"）
 * @param {string} [opts.version="2025-07"] - APIバージョン
 * @param {string} [opts.token] - Storefront Access Token。未指定時は <meta name="shopify-storefront-token"> を参照
 * @param {string} [opts.query=DEFAULT_QUERY] - カスタムクエリを使いたい場合に置換可能
 */
export function createShopifyClient({ shop, version = '2025-07', token, query = DEFAULT_QUERY }) {
  if (!shop) throw new Error('`shop` is required (e.g. "your-shop-subdomain").');

  const ENDPOINT = `https://${shop}.myshopify.com/api/${version}/graphql.json`;
  const TOKEN = token || getTokenFromMeta();
  //console.log(ENDPOINT, TOKEN)
  if (!TOKEN) {
	console.warn('[ShopifyClient] No Storefront token provided. Set opts.token or <meta name="shopify-storefront-token" content="...">');
  }

  /** 1ページだけ取得（低レベル） */
  async function fetchItemsPage({ first = 50, after = null } = {}) {
	const res = await fetch(ENDPOINT, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		'X-Shopify-Storefront-Access-Token': TOKEN
	  },
	  body: JSON.stringify({ query, variables: { first, after } }),
	  credentials: 'same-origin'
	});

	if (!res.ok) {
	  const text = await res.text().catch(() => '');
	  throw new Error(`[StorefrontAPI] ${res.status} ${res.statusText} ${text}`);
	}

	const json = await res.json();
	if (json.errors) {
	  throw new Error(`[StorefrontAPI] ${JSON.stringify(json.errors)}`);
	}
	return json?.data?.metaobjects ?? { edges: [], pageInfo: { hasNextPage: false } };
  }

  /** 全ページをめくって最後まで取得（高レベル） */
  async function fetchAllItems({ pageSize = 50 } = {}) {
	const all = [];
	let after = null;
	let hasNext = true;

	while (hasNext) {
	  const page = await fetchItemsPage({ first: pageSize, after });
	  const edges = page.edges ?? [];
	  edges.forEach(({ node }) => all.push(formatItem(node)));
	  hasNext = page.pageInfo?.hasNextPage;
	  after = edges.length ? edges[edges.length - 1].cursor : null;
	}

	return all;
  }

  return {
	/** 1ページ取得（rawを扱いたいとき） */
	fetchItemsPage,
	/** 全件まとめて取得（整形済み） */
	fetchAllItems
  };
}

/** メタタグからトークンを読む（<meta name="shopify-storefront-token" content="...">） */
export function getTokenFromMeta() {
	const el = document.querySelector('meta[name="shopify-storefront-token"]');
	return el?.getAttribute('content')?.trim() || '';
}

/** 取得した node を使いやすい形に整形 */
export function formatItem(node) {
	//console.log(node)
	const imgs = node?.images?.references?.nodes ?? [];
	// category フィールド展開
	const catRef = node?.category?.reference;
	let category = null;
	if (catRef && catRef.__typename === 'Metaobject') {
		const fields = {};
		for (const f of catRef.fields ?? []) {
		  fields[f.key] = f.value;
		}
		category = {
		  handle: catRef.handle,
		  type: catRef.type,
		  ...fields,
		};
	}
	return {
		handle: node?.handle ?? '',
		priority: node?.priority?.value ?? null,
		url: node?.onlineStoreUrl ?? null,
		title_ja: node?.title_ja?.value ?? '',
		title_en: node?.title_en?.value ?? '',
		is_black: node?.is_black?.value ?? false,
		is_gray: node?.is_gray?.value ?? false,
		description_ja: node?.description_ja?.value ?? '',
		description_en: node?.description_en?.value ?? '',
		initials: node?.initials?.value ?? '',
		category,
		images: imgs
		.filter(n => n.__typename === 'MediaImage' && n.image?.url)
		.map(n => {
			const baseUrl = n.image.url;
			const webpUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}format=webp`;
			return {
			  url: webpUrl,
			  alt: n.image.altText || '',
			  width: n.image.width,
			  height: n.image.height
			};
		})
	};
}

export default {
	createShopifyClient,
	getTokenFromMeta,
	formatItem
};